// ── Unified rep metrics — single source of truth for sales / commission math ──
// Every page that displays rep-level units / revenue / commission numbers must
// read from this module. That keeps the Rep Dashboard, Rep Commission page,
// Admin Reps tab, Admin Commission tab, and customer-detail rollups all in
// lock-step so a dealer invoice counted once on one screen is counted the same
// way on every other screen.
//
// Canonical definition of "a rep's invoice":
//   - not cancelled
//   - AND (inv.rep === repName) OR (inv.customer ∈ rep's customer set)
//
// The rep's customer set is the union of:
//   - admin-assigned accounts (accounts whose ac.rep === repName)
//   - the rep's own route customers (synced via SHARED_REP_CUSTOMERS)
//   - the rep's static customer database (INIT_REP_PROFILES[].customers)
// Building the set once here means one predicate is applied everywhere.

/** Parse an invoice's recorded date. Prefers the ISO sharedAt timestamp stamped
 *  by SHARED_INVOICES.add, falls back to the human date string used by
 *  rep-created invoices. Returns null if neither parses. */
export function parseInvoiceDate(inv) {
  if (!inv) return null;
  if (inv.sharedAt) { const d = new Date(inv.sharedAt); if (!isNaN(d.getTime())) return d; }
  if (inv.date)     { const d = new Date(inv.date);     if (!isNaN(d.getTime())) return d; }
  return null;
}

/** Normalize a company / customer name for comparison — lowercase + trim. */
function normName(s) { return String(s || "").trim().toLowerCase(); }

/** Number coercion that treats total/amount interchangeably. */
function invTotal(inv) { return Number(inv?.total ?? inv?.amount ?? 0) || 0; }

/** Sum of item qty on an invoice (units). */
function invUnits(inv) {
  if (Array.isArray(inv?.items)) return inv.items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  return 0;
}

/** Paid invoice = status paid (shared-invoices flow) or done (legacy order flow). */
function isPaid(inv) { return inv?.status === "paid" || inv?.status === "done"; }

/** Fuzzy rep-name match: exact-normalized OR first-name + last-initial.
 *  Mirrors namesMatchFuzzy in RepPortal.js (kept local here so this file
 *  can be consumed standalone). "Marcus T." on a seeded order still
 *  matches "Marcus Thompson" on the rep profile. Strict enough to avoid
 *  "Marcus T." → "Marcus Vega" collisions. */
export function repNamesMatch(a, b) {
  const na = normName(a), nb = normName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const clean = s => s.replace(/\./g, "").replace(/\s+/g, " ").trim();
  const ca = clean(na), cb = clean(nb);
  if (ca === cb) return true;
  const ta = ca.split(" "), tb = cb.split(" ");
  if (ta[0] !== tb[0] || !ta[0]) return false;
  if (ta.length === 1 || tb.length === 1) return true;
  const la = ta[ta.length - 1], lb = tb[tb.length - 1];
  return !!la.charAt(0) && la.charAt(0) === lb.charAt(0);
}

/** Canonical filter: return the invoices that belong to the given rep.
 *  @param {Array} invoices  all SHARED_INVOICES
 *  @param {string} repName
 *  @param {Iterable<string>} customerNames  rep's customer set (any iterable)
 *  @returns {Array} rep's invoices, cancelled excluded. */
export function filterRepInvoices(invoices, repName, customerNames) {
  if (!repName) return [];
  const nameSet = new Set();
  (customerNames || []).forEach(n => { if (n) nameSet.add(normName(n)); });
  return (invoices || []).filter(inv => {
    if (!inv) return false;
    if (inv.status === "cancelled") return false;
    // Fuzzy match — admin-seeded orders often carry a short rep name
    // ("Marcus T.") while the rep profile is full ("Marcus Thompson").
    // Strict equality missed all of those and wiped commission.
    //
    // Invoice-level rep stamp is AUTHORITATIVE when present. inv.rep
    // records WHO sold the order at the moment it was placed — that's
    // the commission owner. Never fall back to a customer-set match if
    // the stamp is set, because customer sets can overlap across reps
    // (stale customer_settings, deleted-but-still-present
    // rep_route_customers rows, demo seed leftovers). Falling back caused
    // the double-attribution bug where two reps both received commission
    // credit for the same invoice — admin reported "i paid the
    // commission for celine and marcus on the other computer here still
    // showing" because Marcus's invoice was matching both via inv.rep
    // (Marcus) AND via Celine's stale customer-set entry. PR #79 closes
    // that path.
    if (inv.rep) {
      return repNamesMatch(inv.rep, repName);
    }
    // No rep stamp (legacy / customer-portal-direct orders placed before
    // salesRep was wired in): fall back to the rep's customer set as
    // the best available attribution signal.
    return nameSet.has(normName(inv.customer));
  });
}

/** Collect every customer-name a rep "owns" from the three possible sources.
 *  All three are merged so an invoice is counted whether the link came from
 *  admin assignment, rep-portal route edits, or static seed data.
 *  @param {object} opts
 *  @param {string} opts.repName
 *  @param {Array<{company:string,rep?:string}>} [opts.assignedAccounts]  admin accounts
 *  @param {{customers?:Array<{company:string}>}|null} [opts.sharedRepCustomers]  SHARED_REP_CUSTOMERS[repName]
 *  @param {Array<{company:string}>} [opts.staticCustomers]  rep.customers from INIT_REP_PROFILES
 *  @returns {string[]} deduped customer company names */
export function collectRepCustomerNames({ repName, assignedAccounts = [], sharedRepCustomers = null, staticCustomers = [] }) {
  const out = new Set();
  // FIX (2026-04-20): admin's assigned-accounts scope used exact equality on
  // `a.rep === repName`. Admin accounts seeded with "Marcus T." never matched
  // when the rep's live name came through as "Marcus Thompson" — so admin's
  // view saw a narrower invoice set than the rep portal (which uses fuzzy
  // matching everywhere). Unified to repNamesMatch so the admin commission
  // tab sees the same customers the rep does.
  assignedAccounts.forEach(a => { if (repNamesMatch(a.rep || "", repName) && a.company) out.add(a.company); });
  if (sharedRepCustomers?.customers) sharedRepCustomers.customers.forEach(c => { if (c.company) out.add(c.company); });
  staticCustomers.forEach(c => { if (c.company) out.add(c.company); });
  return [...out];
}

/** Sum metrics over an invoice list. Internal — all public helpers call this. */
function summarize(list, rate) {
  const r = Number(rate) || 0;
  const revenue = list.reduce((s, i) => s + invTotal(i), 0);
  const units = list.reduce((s, i) => s + invUnits(i), 0);
  const paidInvs = list.filter(isPaid);
  const paidRevenue = paidInvs.reduce((s, i) => s + invTotal(i), 0);
  return {
    count: list.length,
    paidCount: paidInvs.length,
    units,
    revenue,
    paidRevenue,
    pendingRevenue: revenue - paidRevenue,
    commission: revenue * r / 100,
    commissionPaid: paidRevenue * r / 100,
    commissionPending: (revenue - paidRevenue) * r / 100,
  };
}

/** Full rep dashboard metrics.
 *  @param {object} opts
 *  @param {string} opts.repName
 *  @param {Iterable<string>} opts.customerNames  (use collectRepCustomerNames)
 *  @param {Array} opts.allInvoices               SHARED_INVOICES.getAll()
 *  @param {number} opts.rate                     commission % (e.g. 8)
 *  @param {Date} [opts.asOf]                     for testability; defaults to now */
export function computeRepMetrics({ repName, customerNames, allInvoices, rate, asOf }) {
  const now = asOf instanceof Date ? asOf : new Date();
  const invs = filterRepInvoices(allInvoices, repName, customerNames);
  const year = now.getFullYear(), month = now.getMonth();

  const ytd = invs.filter(i => { const d = parseInvoiceDate(i); return d && d.getFullYear() === year; });
  const thisMonth = invs.filter(i => { const d = parseInvoiceDate(i); return d && d.getFullYear() === year && d.getMonth() === month; });

  const all = summarize(invs, rate);
  const ytdSum = summarize(ytd, rate);
  const monthSum = summarize(thisMonth, rate);

  return {
    rate: Number(rate) || 0,
    invoices: invs,
    // All-time
    totalUnits: all.units,
    totalRevenue: all.revenue,
    totalPaidRevenue: all.paidRevenue,
    totalInvoiceCount: all.count,
    // YTD
    ytdInvoiceCount: ytdSum.count,
    ytdPaidInvoiceCount: ytdSum.paidCount,
    ytdUnits: ytdSum.units,
    ytdRevenue: ytdSum.revenue,
    ytdPaidRevenue: ytdSum.paidRevenue,
    ytdEarned: ytdSum.commission,
    ytdPaidOut: ytdSum.commissionPaid,
    pendingCommission: ytdSum.commissionPending,
    // Current month
    monthUnits: monthSum.units,
    monthRevenue: monthSum.revenue,
    monthCommission: monthSum.commission,
  };
}

/** Summarise a single customer's invoices exactly the way admin + rep portals
 *  both need for the per-customer expansion rows. Pure derivation from
 *  SHARED_INVOICES — no demo-data mixing. */
export function computeCustomerMetrics({ customerName, allInvoices, asOf }) {
  const now = asOf instanceof Date ? asOf : new Date();
  const year = now.getFullYear();
  const norm = normName(customerName);
  const invs = (allInvoices || []).filter(i => i && i.status !== "cancelled" && normName(i.customer) === norm);
  const ytd = invs.filter(i => { const d = parseInvoiceDate(i); return d && d.getFullYear() === year; });
  const week = invs.filter(i => {
    const d = parseInvoiceDate(i);
    return d && (now.getTime() - d.getTime()) <= 7 * 24 * 60 * 60 * 1000;
  });
  const month = invs.filter(i => {
    const d = parseInvoiceDate(i);
    return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });
  return {
    invoices: invs,
    count: invs.length,
    totalRevenue: invs.reduce((s, i) => s + invTotal(i), 0),
    weekRevenue: week.reduce((s, i) => s + invTotal(i), 0),
    monthRevenue: month.reduce((s, i) => s + invTotal(i), 0),
    ytdRevenue: ytd.reduce((s, i) => s + invTotal(i), 0),
    paidCount: invs.filter(isPaid).length,
    pendingCount: invs.filter(i => i.status === "pending").length,
  };
}

// ── UNIFIED INVOICE DISPLAY STATUS ─────────────────────────────────────────
// Single source of truth for the status pill shown on any invoice / order
// across admin, rep, and customer portals. Read in every pill renderer so
// all three views agree on the same label for the same underlying row.
//
// The underlying data model keeps two independent fields (per 0010 migration):
//   - inv.status          — payment axis: paid / pending / payment_pending /
//                           outstanding / cancelled (legacy alias: "done" == paid)
//   - inv.shippingStatus   — fulfillment axis: pending / shipped / delivered /
//                           cancelled
// The 4-value user-facing status collapses those two axes. Cancelled is kept
// as a 5th value because real cancellations exist and can't be hidden.
//
// Precedence (checked top-down; first match wins):
//   1. Cancelled    — status=cancelled (terminal, overrides everything)
//   2. Paid         — status is paid/done (payment received; terminal good-state)
//   3. Shipped      — shippingStatus is shipped or delivered, and not paid
//   4. Overdue      — not paid, not shipped, past dueDate (uses isDueToday helper)
//   5. Pending      — everything else (pending, payment_pending, outstanding,
//                     newly placed orders awaiting processing)

const PILL_COLORS = {
  paid:      { bg:"#DCFCE7", fg:"#166534", border:"#86EFAC" },  // green
  pending:   { bg:"#FEF3C7", fg:"#B45309", border:"#FCD34D" },  // amber
  shipped:   { bg:"#DBEAFE", fg:"#1E40AF", border:"#BFDBFE" },  // blue
  overdue:   { bg:"#FEE2E2", fg:"#B91C1C", border:"#FCA5A5" },  // red
  cancelled: { bg:"#FEE2E2", fg:"#B91C1C", border:"#FCA5A5" },  // red (same as overdue)
};

/** Net-X days parser — mirrors App.js getNetDays so this helper can run
 *  without requiring the caller to pre-compute dueDate. */
function _netDays(terms) {
  if (!terms) return null;
  const m = String(terms).match(/net\s*(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

/** Derive the invoice's due date from explicit dueDate field or from
 *  invoice date + Net-X payment terms. Returns a Date or null. */
function _deriveDueDate(inv) {
  if (!inv) return null;
  if (inv.dueDate) {
    const d = new Date(inv.dueDate);
    if (!isNaN(d.getTime())) return d;
  }
  const days = _netDays(inv.paymentTerms);
  if (days == null || !inv.date) return null;
  const d = new Date(inv.date);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d;
}

/** True if the invoice is unpaid and past its due date. Matches App.js
 *  isOverdue exactly so admin + rep + customer views agree on "overdue". */
function _isOverdue(inv) {
  if (!inv) return false;
  if (inv.status === "paid" || inv.status === "done" || inv.status === "cancelled") return false;
  const due = _deriveDueDate(inv);
  if (!due) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(due); d.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/**
 * Return one of: 'paid' | 'pending' | 'shipped' | 'overdue' | 'cancelled'.
 * This is the SINGLE source of truth. Every pill renderer must call this.
 *
 * @param {object} inv — invoice/order object. Fields read: status,
 *                       shippingStatus, paymentTerms, date, dueDate.
 * @returns {'paid'|'pending'|'shipped'|'overdue'|'cancelled'}
 */
export function deriveInvoiceDisplayStatus(inv) {
  if (!inv) return "pending";
  const payment = inv.status;
  const shipping = inv.shippingStatus;
  if (payment === "cancelled") return "cancelled";
  if (payment === "paid" || payment === "done") return "paid";
  if (shipping === "shipped" || shipping === "delivered") return "shipped";
  if (_isOverdue(inv)) return "overdue";
  return "pending";
}

/** Human label for each display status. */
export function displayStatusLabel(code) {
  return {
    paid:      "Paid",
    pending:   "Pending",
    shipped:   "Shipped",
    overdue:   "Overdue",
    cancelled: "Cancelled",
  }[code] || "Pending";
}

/** Style object for inline pill rendering — bg/fg/border colors picked so
 *  admin, rep, and customer portals all render the same colored pill. */
export function displayStatusPillStyle(code) {
  const c = PILL_COLORS[code] || PILL_COLORS.pending;
  return {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    background: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    whiteSpace: "nowrap",
  };
}

// ── COMMISSION-PAYMENT PARTITIONING ────────────────────────────────────────
// When admin marks invoices paid, each one gets a row in
// rep_commission_payments keyed by invoice_id. To show "paid vs pending" on
// a month bucket we split the period's invoices into the subset that have a
// matching row (paid) and the subset that don't (pending). Legacy fallback:
// if an invoice doesn't have a row but its period WAS marked paid under the
// old localStorage map AND the invoice's date is ≤ paidAt, treat as paid so
// the migration is lossless. Brand-new invoices arriving after paidAt never
// match either check → structurally stay pending.

/**
 * Decide whether a single invoice has been paid commission on. Input:
 *  - inv: the invoice object (needs id + a parseable date)
 *  - paidInvoiceIds: Set of invoice_id strings that have Supabase rows
 *  - legacyPaidPeriodsForRep: { [periodLabel]: { paidAt:ISO, ... } } — the
 *      old mpcare_commission_paid_periods map narrowed to the one rep.
 * Returns a string tag: 'supabase' | 'legacy' | null.
 */
export function classifyInvoicePayoutStatus(inv, paidInvoiceIds, legacyPaidPeriodsForRep) {
  if (!inv || !inv.id) return null;
  if (paidInvoiceIds && paidInvoiceIds.has(inv.id)) return 'supabase';
  if (legacyPaidPeriodsForRep) {
    const d = parseInvoiceDate(inv);
    if (d) {
      const periodLabel = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      const entry = legacyPaidPeriodsForRep[periodLabel];
      if (entry && entry.paidAt) {
        const paidAt = new Date(entry.paidAt);
        if (!isNaN(paidAt.getTime())) {
          // Prefer the actual invoice CREATION timestamp (`sharedAt`, stamped
          // by SHARED_INVOICES.add) — that's the unambiguous "did this exist
          // when admin clicked Mark Paid?" signal. Fall back to the invoice
          // date (which parses to midnight, no time-of-day) ONLY for old
          // invoices that pre-date sharedAt being added, and use STRICT < to
          // avoid same-day collisions where a new invoice's midnight-of-Apr-20
          // is otherwise ≤ admin's Apr 20 14:23 click and gets bucketed as
          // legacy-paid. That timestamp collision was producing two visible
          // bugs: new orders appeared as Paid in the admin Commission tab,
          // and the rep's Commission page showed them under the Legacy batch
          // instead of in Pending payment.
          const created = inv.sharedAt ? new Date(inv.sharedAt) : null;
          if (created && !isNaN(created.getTime())) {
            // Have exact creation timestamp — strict timestamp comparison is safe.
            if (created.getTime() < paidAt.getTime()) return 'legacy';
          } else {
            // No sharedAt — fall back to day-level comparison. Invoice date parses
            // to midnight, so a timestamp compare would wrongly classify any same-
            // day invoice as legacy-paid. Compare calendar days to be conservative:
            // same-day = pending, earlier-day = paid.
            const invDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
            const paidDay = new Date(paidAt.getFullYear(), paidAt.getMonth(), paidAt.getDate()).getTime();
            if (invDay < paidDay) return 'legacy';
          }
        }
      }
    }
  }
  return null;
}

/**
 * Split a period (as returned by groupInvoicesByMonth) into paidInvoices /
 * pendingInvoices, with commission totals for each slice. This is the
 * partial-period model that replaces the old all-or-nothing per-period flag.
 *
 * @param {object} period  one entry from groupInvoicesByMonth()
 * @param {number} rate    commission rate (%)
 * @param {Set<string>} paidInvoiceIds  invoice ids with a row in rep_commission_payments
 * @param {object} legacyPaidPeriodsForRep  the rep's slice of the old localStorage map
 * @returns {{paidInvoices, pendingInvoices, commissionPaid, commissionPending, salesPaid, salesPending, status}}
 *   status is 'paid' (all paid), 'pending' (none paid), or 'partial'.
 */
export function partitionPeriodByPaidInvoices(period, rate, paidInvoiceIds, legacyPaidPeriodsForRep) {
  const r = Number(rate) || 0;
  const paidInvoices = [];
  const pendingInvoices = [];
  (period?.invoices || []).forEach(inv => {
    const tag = classifyInvoicePayoutStatus(inv, paidInvoiceIds, legacyPaidPeriodsForRep);
    if (tag) paidInvoices.push(inv); else pendingInvoices.push(inv);
  });
  const salesPaid = paidInvoices.reduce((s, i) => s + (Number(i?.total ?? i?.amount ?? 0) || 0), 0);
  const salesPending = pendingInvoices.reduce((s, i) => s + (Number(i?.total ?? i?.amount ?? 0) || 0), 0);
  const total = paidInvoices.length + pendingInvoices.length;
  const status = total === 0 ? 'pending'
              : paidInvoices.length === total ? 'paid'
              : pendingInvoices.length === total ? 'pending'
              : 'partial';
  return {
    paidInvoices,
    pendingInvoices,
    salesPaid,
    salesPending,
    commissionPaid:    salesPaid * r / 100,
    commissionPending: salesPending * r / 100,
    status,
  };
}

// ── BATCH-BASED COMMISSION VIEW ────────────────────────────────────────────
// The commission UI was period-rollup (one row per "April 2026") with
// paid/partial/pending status. Admins asked for batch-based: every click of
// Mark Paid freezes a historical "payout" row with that click's invoices, and
// any invoice arriving AFTER that click shows up in a fresh pending bucket.
// This matches how real payroll works: you cut a check for what's due today,
// tomorrow's sales accrue into the next check.
//
// Data sources:
//   • rep_commission_payments rows (Supabase, one per invoice per payout,
//     grouped by batch_id) — the source of truth for paid invoices.
//   • legacyPaidMap (mpcare_commission_paid_periods localStorage) — pre-PR-48
//     paid-period markers, kept as a fallback so pre-deploy paid periods
//     don't flip back to pending.
//   • all invoices belonging to the rep (filtered elsewhere via
//     filterRepInvoices). Inputs here are already scoped to one rep.

/**
 * Group commission-payment rows into batches, one batch per batch_id.
 * Each batch knows which invoice ids are in it, the payout metadata
 * (paidAt / method / notes), and the total commission amount.
 * @param {Array} paidRows  rep_commission_payments rows (already scoped)
 * @returns {Array<{batchId,paidAt,method,notes,invoiceIds:Set,
 *                  commission,invoiceCount,firstRow}>}
 */
export function groupPaidInvoicesByBatch(paidRows) {
  const byBatch = {};
  (paidRows || []).forEach(r => {
    if (!r || !r.invoice_id) return;
    const bid = r.batch_id || `__no_batch__${r.invoice_id}`;
    if (!byBatch[bid]) {
      byBatch[bid] = {
        batchId: bid,
        paidAt: r.paid_at || null,
        method: r.method || "",
        notes: r.notes || "",
        // The real, admin-set period for this batch (written once per Mark
        // Paid click, App.js). Previously never read here, so every real
        // (non-legacy) batch fell through to a renderer-side GUESS at the
        // period on the rep's own Commission > Payment history table
        // (RepPortal.js:5319, deriving it from an arbitrary invoice's own
        // date) instead of the correctly-stored value — the likely cause of
        // a payout showing a "period" that looks inconsistent with its own
        // paid date. Admin's own paid-batch tables (App.js) do not currently
        // render a period column for a paid batch at all, so this fix is
        // scoped to the rep-facing screen; App.js's `paid` array still
        // carries the now-correct periodLabel through (App.js:4887) if an
        // admin period column is ever added there.
        periodLabel: r.period || null,
        invoiceIds: new Set(),
        commission: 0,
        invoiceCount: 0,
        firstRow: r,
      };
    }
    const b = byBatch[bid];
    b.invoiceIds.add(String(r.invoice_id));
    b.commission += Number(r.commission_amount) || 0;
    b.invoiceCount = b.invoiceIds.size;
    // Prefer the latest-paid-at within the batch for display.
    if (r.paid_at && (!b.paidAt || new Date(r.paid_at) > new Date(b.paidAt))) {
      b.paidAt = r.paid_at;
      b.method = r.method || b.method;
      b.notes = r.notes || b.notes;
      b.periodLabel = r.period || b.periodLabel;
    }
  });
  // Sort batches by paidAt desc — most recent payout first.
  return Object.values(byBatch).sort((a, b) => {
    const ta = a.paidAt ? new Date(a.paidAt).getTime() : 0;
    const tb = b.paidAt ? new Date(b.paidAt).getTime() : 0;
    return tb - ta;
  });
}

/**
 * Given a rep's full invoice list and the paid-row set, split into:
 *   - paidBatches: array of batch records (with their full invoice objects
 *                  resolved from allInvoices by id)
 *   - pendingInvoices: invoices not in any batch AND not covered by a
 *                       legacy-paid cutoff. These are what the rep is
 *                       currently owed commission on.
 * Legacy fallback: if an invoice's id isn't in any rep_commission_payments
 * row but its date ≤ legacy paidAt for its month, treat it as legacy-paid
 * under a synthetic batch labeled "legacy-{period}" so existing pre-deploy
 * paid periods still render as paid (grouped).
 *
 * @param {Array} invoices   rep's invoices (already filtered to this rep)
 * @param {number} rate      commission rate (%)
 * @param {Array} paidRows   rep_commission_payments rows (already scoped)
 * @param {object} legacyPaidMap  { [periodLabel]: { paidAt, date, method, notes } }
 */
export function batchizeRepInvoices(invoices, rate, paidRows, legacyPaidMap) {
  const r = Number(rate) || 0;
  const batches = groupPaidInvoicesByBatch(paidRows);
  const invoiceIdsInAnyBatch = new Set();
  batches.forEach(b => b.invoiceIds.forEach(id => invoiceIdsInAnyBatch.add(id)));
  // Resolve invoice objects for each batch so the UI can show line items
  // without a second lookup.
  const invById = new Map();
  (invoices || []).forEach(inv => { if (inv?.id != null) invById.set(String(inv.id), inv); });
  const batchesWithInvoices = batches.map(b => ({
    ...b,
    invoices: [...b.invoiceIds].map(id => invById.get(id)).filter(Boolean),
  }));
  // FIX (2026-04-20): determine which periods already have Supabase coverage.
  // Legacy mpcare_commission_paid_periods localStorage is per-device, so two
  // browsers can have different paidAt for the same period — one admin's
  // browser says "April 2026 paid through Apr 18" and a rep's browser says
  // "April 2026 paid through Apr 20", producing contradictory classification
  // for the same Apr 19 invoice. Once admin has backfilled a period into
  // Supabase rep_commission_payments, that period's Supabase batch is the
  // authoritative record — any invoice not in it is pending, regardless
  // of what the stale localStorage map says. Walk batch invoices to derive
  // covered periods; unresolvable batch entries fall back to the raw paid
  // rows' invoice ids (we can't derive their period without the invoice
  // object, but any invoice in the covered period that DOES exist here
  // produces the correct override).
  const supabaseCoveredPeriods = new Set();
  batchesWithInvoices.forEach(b => {
    (b.invoices || []).forEach(inv => {
      const d = parseInvoiceDate(inv);
      if (d) supabaseCoveredPeriods.add(d.toLocaleDateString("en-US", { month: "long", year: "numeric" }));
    });
  });
  // Walk pending candidates, splitting off legacy-paid ones into synthetic
  // batches. Every real batch gets a batch_id from Supabase; legacy ones
  // use `legacy-{periodLabel}` so they don't collide.
  const pendingInvoices = [];
  const legacyBuckets = {};   // periodLabel → { paidAt, invoices }
  (invoices || []).forEach(inv => {
    if (!inv || inv.status === "cancelled") return;
    const id = String(inv.id || "");
    if (!id) return;
    if (invoiceIdsInAnyBatch.has(id)) return; // already captured by a real batch
    const d = parseInvoiceDate(inv);
    if (legacyPaidMap && d) {
      const periodLabel = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      // Skip legacy fallback when Supabase already covers this period —
      // that's the fix described above.
      if (!supabaseCoveredPeriods.has(periodLabel)) {
        const entry = legacyPaidMap[periodLabel];
        if (entry && entry.paidAt) {
          const paidAt = new Date(entry.paidAt);
          // Same fix as classifyInvoicePayoutStatus: use the invoice's actual
          // creation timestamp (sharedAt) when available, fall back to the
          // invoice date with STRICT < (not ≤) to avoid same-day collisions.
          // Without this, a new invoice created on the same day admin clicked
          // Mark Paid was wrongly absorbed into the Legacy batch — which is
          // why new orders kept appearing as Paid in the admin Commission tab
          // and why the rep saw an empty Pending payment section.
          const createdTs = inv.sharedAt ? new Date(inv.sharedAt).getTime() : NaN;
          let isLegacyPaid = false;
          if (!isNaN(paidAt.getTime())) {
            if (!isNaN(createdTs)) {
              // Exact creation timestamp available — strict timestamp compare.
              isLegacyPaid = createdTs < paidAt.getTime();
            } else {
              // No sharedAt — day-level comparison so same-day doesn't collide.
              const invDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
              const paidDay = new Date(paidAt.getFullYear(), paidAt.getMonth(), paidAt.getDate()).getTime();
              isLegacyPaid = invDay < paidDay;
            }
          }
          if (isLegacyPaid) {
            const key = periodLabel;
            if (!legacyBuckets[key]) legacyBuckets[key] = {
              batchId: `legacy-${periodLabel.replace(/\s+/g,"-").toLowerCase()}`,
              paidAt: entry.paidAt,
              method: entry.method || "",
              notes: entry.notes ? `${entry.notes} (legacy)` : "Legacy payout (pre-migration)",
              invoices: [],
              invoiceCount: 0,
              commission: 0,
              legacy: true,
              periodLabel,
            };
            legacyBuckets[key].invoices.push(inv);
            legacyBuckets[key].invoiceCount = legacyBuckets[key].invoices.length;
            legacyBuckets[key].commission += (Number(inv.total) || 0) * r / 100;
            return;
          }
        }
      }
    }
    pendingInvoices.push(inv);
  });
  const legacyBatchList = Object.values(legacyBuckets).sort((a,b) => {
    const ta = a.paidAt ? new Date(a.paidAt).getTime() : 0;
    const tb = b.paidAt ? new Date(b.paidAt).getTime() : 0;
    return tb - ta;
  });
  // Combine real + legacy batches. Real first (most recent), then legacy.
  const allBatches = [...batchesWithInvoices, ...legacyBatchList];
  // Group pending invoices by month label for display.
  const pendingByPeriod = {};
  pendingInvoices.forEach(inv => {
    const d = parseInvoiceDate(inv);
    if (!d) return;
    const periodLabel = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!pendingByPeriod[periodLabel]) pendingByPeriod[periodLabel] = {
      periodLabel,
      year: d.getFullYear(),
      month: d.getMonth(),
      invoices: [],
      sales: 0,
      commission: 0,
    };
    pendingByPeriod[periodLabel].invoices.push(inv);
    pendingByPeriod[periodLabel].sales += Number(inv.total) || 0;
    pendingByPeriod[periodLabel].commission += (Number(inv.total) || 0) * r / 100;
  });
  const pendingPeriodsList = Object.values(pendingByPeriod).sort((a,b) =>
    (b.year - a.year) || (b.month - a.month)
  );
  return {
    batches: allBatches,
    pendingInvoices,           // flat list
    pendingPeriods: pendingPeriodsList,   // grouped by month for display
    totalPaid: allBatches.reduce((s,b) => s + b.commission, 0),
    totalPending: pendingInvoices.reduce((s,inv) => s + (Number(inv.total)||0) * r / 100, 0),
  };
}

/** Group YTD invoices into period buckets ("April 2025" etc.) for the
 *  commission payment tables. Rate applied uniformly across periods. */
export function groupInvoicesByMonth(invoices, rate) {
  const r = Number(rate) || 0;
  const periodMap = {};
  (invoices || []).forEach(inv => {
    const d = parseInvoiceDate(inv);
    if (!d) return;
    const key = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (!periodMap[key]) {
      periodMap[key] = {
        period: key, year: d.getFullYear(), month: d.getMonth(),
        invoices: [], paidInvoices: [], pendingInvoices: [],
      };
    }
    periodMap[key].invoices.push(inv);
    if (isPaid(inv)) periodMap[key].paidInvoices.push(inv);
    else periodMap[key].pendingInvoices.push(inv);
  });
  return Object.values(periodMap)
    .map(p => {
      const salesAll = p.invoices.reduce((s, i) => s + invTotal(i), 0);
      const salesPaid = p.paidInvoices.reduce((s, i) => s + invTotal(i), 0);
      const salesPending = p.pendingInvoices.reduce((s, i) => s + invTotal(i), 0);
      return {
        ...p,
        salesAll, salesPaid, salesPending,
        commissionAll: salesAll * r / 100,
        commissionPaid: salesPaid * r / 100,
        commissionPending: salesPending * r / 100,
      };
    })
    .sort((a, b) => (b.year - a.year) || (b.month - a.month));
}
